/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Project } from '@/types/database'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Fetch ALL projects from the platform (admin only)
 * This uses the admin API endpoint which bypasses RLS
 * and returns all projects regardless of ownership.
 *
 * Only accessible if the user has is_admin = TRUE
 */
export function useAdminProjects() {
  return useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Not authenticated')
      }

      // Call the admin API endpoint
      const response = await fetch(`${API_BASE}/api/admin/projects`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required')
        }
        const errorText = await response.text()
        throw new Error(`Failed to fetch projects: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      return data as Project[]
    },
  })
}
